import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Sparkles, Lock, ArrowRight, X, Clock, ShieldAlert } from "lucide-react";

/**
 * Global upgrade-required modal. Listens for the `skillgap:limit-reached`
 * CustomEvent dispatched by the axios interceptor (see /app/frontend/src/lib/api.js)
 * whenever the backend returns HTTP 402 with a `USAGE_LIMIT_REACHED` or
 * `SUBSCRIPTION_EXPIRED` detail. Mount once in the AppShell.
 */
export default function UpgradeModal() {
  const [payload, setPayload] = useState(null);
  const navigate = useNavigate();

  useEffect(() => {
    const handler = (e) => setPayload(e.detail || null);
    window.addEventListener("skillgap:limit-reached", handler);
    return () => window.removeEventListener("skillgap:limit-reached", handler);
  }, []);

  if (!payload) return null;

  const expired = payload.code === "SUBSCRIPTION_EXPIRED";
  const title = expired ? "Your subscription has expired" : "Free plan limit reached";
  const featureLabel = payload.feature_label || "This feature";

  const goPricing = () => {
    setPayload(null);
    navigate(payload.redirect || "/app/pricing");
  };
  const close = () => setPayload(null);

  return (
    <div
      className="fixed inset-0 z-[1000] flex items-center justify-center bg-black/60 backdrop-blur-sm px-4"
      data-testid="upgrade-modal"
      onClick={close}
    >
      <div
        role="dialog"
        aria-modal="true"
        className="relative w-full max-w-md rounded-2xl bg-white shadow-2xl overflow-hidden"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="absolute -right-16 -top-16 h-48 w-48 rounded-full bg-gradient-to-br from-indigo-500 via-sky-500 to-emerald-500 opacity-25 blur-2xl pointer-events-none" />
        <button
          onClick={close}
          data-testid="upgrade-modal-close"
          aria-label="Close"
          className="absolute right-3 top-3 h-8 w-8 inline-flex items-center justify-center rounded-md text-zinc-400 hover:text-zinc-800 hover:bg-zinc-100"
        >
          <X size={16} />
        </button>
        <div className="relative p-7">
          <div className={`h-12 w-12 rounded-full flex items-center justify-center ${expired ? "bg-rose-100 text-rose-600" : "bg-indigo-100 text-indigo-600"}`}>
            {expired ? <Clock size={22} /> : <Lock size={22} />}
          </div>
          <h2 className="font-display font-black text-2xl tracking-tight mt-4" data-testid="upgrade-modal-title">
            {title}
          </h2>
          <p className="text-sm text-zinc-600 mt-2 leading-relaxed" data-testid="upgrade-modal-message">
            {payload.message || `Upgrade to continue using ${featureLabel}.`}
          </p>

          {!expired && typeof payload.used === "number" && typeof payload.limit === "number" && (
            <div className="mt-4 flex items-center gap-3 rounded-lg border border-zinc-200 bg-zinc-50 px-4 py-3">
              <ShieldAlert size={16} className="text-zinc-500" />
              <div className="text-xs text-zinc-700">
                <span className="font-semibold">{featureLabel}:</span>{" "}
                <span className="font-mono">{payload.used}/{payload.limit}</span> used on Free plan
              </div>
            </div>
          )}

          <button
            onClick={goPricing}
            data-testid="upgrade-modal-cta"
            className="mt-6 w-full h-11 rounded-md bg-gradient-to-r from-indigo-600 via-sky-500 to-emerald-500 text-white font-semibold hover:opacity-90 transition inline-flex items-center justify-center gap-2"
          >
            <Sparkles size={14} /> {expired ? "Renew now" : "View plans"} <ArrowRight size={14} />
          </button>
          <button
            onClick={close}
            data-testid="upgrade-modal-dismiss"
            className="mt-2 w-full h-10 rounded-md text-sm text-zinc-600 hover:bg-zinc-100"
          >
            Maybe later
          </button>
        </div>
      </div>
    </div>
  );
}
